// GET /api/business/v1/config — feature flags, content and settings from
// ihjizli-bff-business (in node mode erp-core's localbook serves the same
// shape, flags from the licence). Shape (bff-business aggregator.ConfigPayload):
//   { version, generated_at, flags: { "erp.core": true, … }, content: {…}, settings: {…} }
//
// FAIL CLOSED (BRIEF): a flag that is missing, non-boolean, or a config that
// failed to load reads as OFF. Rule 6: off ⇒ the element is not rendered.
// Online, the erp.* keys are overlaid by the tenant-effective set from
// GET /v1/business/features (lib/features.ts, merged in app/erp-context.tsx).

import { useQuery } from '@tanstack/react-query';
import { businessApi } from './api';
import { maintenanceFromConfig, maintenanceStore, type MaintenanceWindow } from './maintenance';

export interface AppConfig {
  version?: string;
  generated_at?: string;
  flags: Record<string, boolean>;
  content?: Record<string, unknown>;
  settings?: Record<string, unknown> | null;
  /** The scheduled-maintenance window this payload reports (lib/maintenance.ts). */
  maintenance: MaintenanceWindow;
}

/** Known ERP flags (HANDOFF-erp-web §8). `erp.bank-import.<bank>` is open-ended. */
export const ERP_FLAGS = [
  'erp.core',
  'erp.accounts',
  'erp.inventory',
  'erp.installments',
  'erp.reports',
  'erp.team',
  'erp.branches',
  'erp.medical',
  'erp.tasks',
  'erp.print',
  'erp.store',
  'erp.local-mode',
  // Phase 0 feature control (platform registry) — keys only, no routes yet.
  'erp.hotel',
  'erp.channels',
  'erp.restaurant',
  'erp.pharmacy',
  'erp.prescriptions',
  'erp.wholesale',
  'erp.lab',
  'erp.radiology',
  'erp.referrals',
  'erp.marketing',
  'erp.settlements',
  'erp.devices',
  'erp.assistant',
] as const;
export type ErpFlag = (typeof ERP_FLAGS)[number] | `erp.bank-import.${string}`;

/** Normalise any payload into a flags map that only contains real `true`s. */
export function normalizeFlags(raw: unknown): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  const flags = raw && typeof raw === 'object' ? (raw as { flags?: unknown }).flags : undefined;
  if (!flags || typeof flags !== 'object') return out;
  for (const [k, v] of Object.entries(flags as Record<string, unknown>)) if (v === true) out[k] = true;
  return out;
}

/** Fail-closed lookup: true only when the flag is literally true. null flag = no gate. */
export function flagOn(flags: Record<string, boolean> | null | undefined, flag: string | null | undefined): boolean {
  if (!flag) return true;
  return flags?.[flag] === true;
}

export const CONFIG_QUERY_KEY = ['business', 'config'] as const;

export async function fetchConfig(): Promise<AppConfig> {
  const raw = await businessApi.get<unknown>('/v1/config');
  const obj = (raw && typeof raw === 'object' ? raw : {}) as Partial<AppConfig>;
  return {
    version: obj.version,
    generated_at: obj.generated_at,
    flags: normalizeFlags(raw),
    content: obj.content ?? {},
    settings: obj.settings ?? null,
    maintenance: reportMaintenance(raw),
  };
}

/** Read the window from a config payload and tell the store (the app root redirects on it). */
function reportMaintenance(raw: unknown): MaintenanceWindow {
  const w = maintenanceFromConfig(raw);
  maintenanceStore.report(w);
  return w;
}

export function useConfigQuery(enabled = true) {
  return useQuery({ queryKey: CONFIG_QUERY_KEY, queryFn: fetchConfig, enabled, staleTime: 5 * 60_000, retry: 1 });
}
